import {
  DEFAULT_DATE_FORMAT_FNS,
  Group,
  useGetMembersForGroupQuery,
  UserWithGroupMembership,
} from '@schooly/api';
import { SchoolUserRole } from '@schooly/constants';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { useMemo } from 'react';

import { GroupGridMember } from '../common/buildGroupGrid';

const toDateKey = (value: string): string => value.slice(0, 10);

const toMembers = (users: UserWithGroupMembership[]): GroupGridMember[] => {
  const byRelationId = new Map<string, GroupGridMember>();

  for (const user of users) {
    const interval = { start: toDateKey(user.start), end: user.end ? toDateKey(user.end) : null };
    const existing = byRelationId.get(user.relation_id);
    if (existing) {
      existing.memberships.push(interval);
      continue;
    }

    byRelationId.set(user.relation_id, {
      relation_id: user.relation_id,
      given_name: user.given_name ?? null,
      last_name: user.last_name,
      known_as: null,
      profile_picture: user.profile_picture ?? null,
      memberships: [interval],
    });
  }

  return [...byRelationId.values()];
};

export const useGroupStudentsForValidity = (group: Group) => {
  const dateFrom = group.validity?.date_from ?? group.validity?.from_school_year?.start ?? '';
  const dateTo = format(newDateTimezoneOffset(), DEFAULT_DATE_FORMAT_FNS);
  const enabled = !!dateFrom;

  const { data, isLoading } = useGetMembersForGroupQuery(
    { id: group.id, date_from: dateFrom, date_to: dateTo, user_role: SchoolUserRole.Student },
    { enabled, refetchOnMount: 'always' },
  );

  const members = useMemo(() => toMembers(data?.users ?? []), [data?.users]);

  return { members, isLoading };
};
